// src/features/tickets/hooks/useCustomerTickets.ts
//
// Rein lesender Hook: laedt die juengsten weiteren Tickets desselben Kunden
// (gleiches customer_id), ohne das aktuell geoeffnete Ticket. Versorgt den
// Kontext-Abschnitt "Weitere Tickets dieses Kunden" im SidePanel (F6, Teil 1).
//
// Keine Domain-Logik (Teil 2 folgt separat), keine PostgREST-Embeds, kein
// Backend — eine einfache Abfrage auf tickets, analog zum Muster von
// useCustomerDeadlines.

import { useState, useEffect } from 'react';
import { supabase } from '../../../shared/services/supabase';
import { Ticket, TicketStatus } from '../types';

export interface CustomerTicketSummary {
  id: string;
  ticket_number: string;
  subject: string;
  status: TicketStatus;
  last_activity: string; // last_message_at, sonst created_at
}

export interface UseCustomerTicketsResult {
  loading: boolean;
  tickets: CustomerTicketSummary[];
}

const MAX_TICKETS = 8;

export const useCustomerTickets = (
  customerId: string | null,
  currentTicketId: string
): UseCustomerTicketsResult => {
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState<CustomerTicketSummary[]>([]);

  useEffect(() => {
    if (!customerId) {
      setTickets([]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('tickets')
          .select('id, ticket_number, subject, status, last_message_at, created_at')
          .eq('customer_id', customerId)
          .neq('id', currentTicketId)
          .order('last_message_at', { ascending: false, nullsFirst: false })
          .order('created_at', { ascending: false })
          .limit(MAX_TICKETS);

        if (cancelled) return;

        if (error) {
          console.error('[useCustomerTickets] Load error:', error);
          setTickets([]);
          return;
        }

        const rows = (data ?? []) as Array<
          Pick<Ticket, 'id' | 'ticket_number' | 'subject' | 'status' | 'last_message_at' | 'created_at'>
        >;
        setTickets(
          rows.map(t => ({
            id: t.id,
            ticket_number: t.ticket_number,
            subject: t.subject,
            status: t.status,
            last_activity: t.last_message_at ?? t.created_at
          }))
        );
      } catch (err) {
        console.error('[useCustomerTickets] Unexpected error:', err);
        if (!cancelled) setTickets([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [customerId, currentTicketId]);

  return { loading, tickets };
};
